import type { StoryStep, StepShape } from '../editor/storyModel';

export interface StoryMarkdownDocument {
  globalHeader: string;
  steps: StoryStep[];
}

const STEP_SEPARATOR = /(?:^|\r?\n)\s*---\s*(?:\r?\n|$)/;
const GLOBAL_HEADER = /^(?:\uFEFF)?---GLOBAL---\s*\r?\n[\s\S]*?\r?\n---\s*(?:\r?\n)*/;
const KNOWN_SHAPES = new Set<StepShape>([
  'sphere', 'tile', 'hexagon', 'box', 'cylinder', 'plane', 'gltf',
]);

function stableId(block: string, index: number): string {
  let hash = 2166136261;
  for (let cursor = 0; cursor < block.length; cursor += 1) {
    hash ^= block.charCodeAt(cursor);
    hash = Math.imul(hash, 16777619);
  }
  return `step-${index + 1}-${(hash >>> 0).toString(36)}`;
}

function parseMetadata(block: string): {
  content: string;
  metadata: Record<string, string>;
  metadataOrder: string[];
  metadataExtraLines: string[];
} {
  const comment = block.match(/<!--([\s\S]*?)-->/);
  if (!comment) {
    return { content: block.trim(), metadata: {}, metadataOrder: [], metadataExtraLines: [] };
  }

  const metadata: Record<string, string> = {};
  const metadataOrder: string[] = [];
  const metadataExtraLines: string[] = [];
  comment[1].split(/\r?\n/).forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) return;
    const colon = line.indexOf(':');
    if (colon < 1) {
      metadataExtraLines.push(line);
      return;
    }
    const key = line.slice(0, colon).trim();
    metadata[key] = line.slice(colon + 1).trim();
    if (!metadataOrder.includes(key)) metadataOrder.push(key);
  });

  return {
    content: block.replace(comment[0], '').trim(),
    metadata,
    metadataOrder,
    metadataExtraLines,
  };
}

function parseStep(block: string, index: number): StoryStep {
  const parsed = parseMetadata(block);
  const titleMatch = parsed.content.match(/^#\s+(.+)$/m);
  const title = titleMatch?.[1]?.trim() || `Station ${index + 1}`;
  const body = titleMatch
    ? parsed.content.replace(titleMatch[0], '').replace(/^\s+/, '').trimEnd()
    : parsed.content;
  const rawShape = parsed.metadata.shape || parsed.metadata.type;
  const scale = Number.parseFloat(parsed.metadata.scale || '');

  return {
    id: parsed.metadata.editorId || stableId(block, index),
    title,
    body,
    shape: KNOWN_SHAPES.has(rawShape as StepShape) ? rawShape as StepShape : undefined,
    color: parsed.metadata.color || undefined,
    uri: parsed.metadata.uri || undefined,
    scale: Number.isFinite(scale) ? scale : undefined,
    metadata: parsed.metadata,
    metadataOrder: parsed.metadataOrder,
    metadataExtraLines: parsed.metadataExtraLines,
  };
}

export function parseStoryMarkdown(markdown: string): StoryMarkdownDocument {
  const source = markdown.replace(/\r\n/g, '\n');
  const globalMatch = source.match(GLOBAL_HEADER);
  const globalHeader = globalMatch?.[0]?.trimEnd() || '';
  const storySource = globalMatch ? source.slice(globalMatch[0].length) : source;
  const blocks = storySource
    .split(STEP_SEPARATOR)
    .map((block) => block.trim())
    .filter(Boolean);

  return {
    globalHeader,
    steps: blocks.map(parseStep),
  };
}

function metadataForStep(step: StoryStep): string[] {
  const metadata = { ...(step.metadata || {}) };
  const order = [...(step.metadataOrder || [])];
  const set = (key: string, value: string | number | undefined) => {
    if (value === undefined || value === '') {
      delete metadata[key];
      return;
    }
    metadata[key] = String(value);
    if (!order.includes(key)) order.push(key);
  };

  set('shape', step.shape);
  set('color', step.color);
  set('uri', step.uri);
  set('scale', step.scale);

  return [
    ...order.filter((key) => metadata[key] !== undefined).map((key) => `${key}: ${metadata[key]}`),
    ...(step.metadataExtraLines || []),
  ];
}

function serializeStep(step: StoryStep): string {
  const metadata = metadataForStep(step);
  const comment = metadata.length > 0 ? `<!--\n${metadata.join('\n')}\n-->\n` : '';
  const body = step.body.trim();
  return `${comment}# ${step.title.trim() || 'Ohne Titel'}${body ? `\n\n${body}` : ''}`;
}

export function serializeStoryMarkdown(document: StoryMarkdownDocument): string {
  const story = document.steps.map(serializeStep).join('\n\n---\n\n');
  return `${document.globalHeader ? `${document.globalHeader}\n\n` : ''}${story}${story ? '\n' : ''}`;
}

